import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Sparkles, Heart, MessageCircle, User, Crown, Settings,
  BarChart3, Trophy, Gift, LogOut, ShoppingBag, ExternalLink
} from "lucide-react";
import { MerchLaunchBanner } from "./MerchLaunchBanner";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationContext";

const serif = { fontFamily: "'Playfair Display', serif" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };
const NAVY = "#0A2A5A";
const GOLD = "#F9AB0D";

const cleanTimeLabel = (cleanDate) => {
  if (!cleanDate) return null;
  const start = new Date(cleanDate);
  if (isNaN(start)) return null;
  const months = Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
  if (months < 1) return "Day one strong";
  if (months < 12) return `${months} mo clean`;
  const years = Math.floor(months / 12);
  return `${years} yr${years > 1 ? "s" : ""} clean`;
};

const NavItem = ({ to, icon: Icon, label, badge, end }) => (
  <NavLink
    to={to}
    end={end}
    className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors"
    style={({ isActive }) =>
      isActive
        ? { backgroundColor: "rgba(249,171,13,0.15)", color: GOLD }
        : { color: "rgba(255,255,255,0.65)" }
    }
    data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    {({ isActive }) => (
      <>
        <Icon size={20} />
        <span className={`text-sm ${isActive ? "font-bold" : "font-medium"}`}>{label}</span>
        {badge > 0 && (
          <span
            className="ml-auto text-[10px] font-extrabold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center"
            style={{ backgroundColor: GOLD, color: NAVY }}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </>
    )}
  </NavLink>
);

const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const { unreadMessages, newMatches } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  const isPaid = ["recovery_plus", "recovery_elite", "gold", "platinum"].includes(user?.subscription_tier);
  const cleanLabel = cleanTimeLabel(user?.clean_date);
  const photo = Array.isArray(user?.photos) ? user.photos[0] : null;
  const isChat = location.pathname.startsWith("/chat/");

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9F6F2" }}>
      {/* Desktop navy sidebar */}
      <aside
        className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col z-40"
        style={{ backgroundColor: NAVY }}
        data-testid="app-sidebar"
      >
        <button
          onClick={() => navigate("/matches")}
          className="px-7 pt-8 pb-8 flex items-center gap-3 text-left"
          data-testid="sidebar-logo"
        >
          <img src="/images/logo-icon.png" alt="RecoveryMatch" className="w-9 h-9 rounded-lg" />
          <p className="text-xl font-bold" style={serif}>
            <span className="text-white">Recovery</span>
            <span style={{ color: GOLD }}>Match</span>
          </p>
        </button>

        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          <NavItem to="/matches" icon={Sparkles} label="Matches" badge={newMatches} />
          <NavItem to="/likes" icon={Heart} label="Likes" />
          <NavItem to="/messages" icon={MessageCircle} label="Messages" badge={unreadMessages} />
          <NavItem to="/profile" icon={User} label="Profile" end />

          <div className="pt-5 pb-1 px-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ ...mono, color: "rgba(255,255,255,0.35)" }}>
              More
            </p>
          </div>
          <NavItem to="/insights" icon={BarChart3} label="Insights" />
          <NavItem to="/leaderboard" icon={Trophy} label="Leaderboard" />
          <NavItem to="/gift-shop" icon={Gift} label="Gift Shop" />
          <a
            href="https://shop.recoverymatch.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors hover:bg-white/5"
            style={{ color: "rgba(255,255,255,0.65)" }}
            data-testid="sidebar-nav-merch-shop"
          >
            <ShoppingBag size={20} />
            <span className="text-sm font-medium">Merch Shop</span>
            <ExternalLink size={13} className="ml-auto opacity-50" />
          </a>
          <NavItem to="/settings" icon={Settings} label="Settings" />
        </nav>

        {!isPaid && (
          <div className="mx-4 mb-2 rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(249,171,13,0.3)" }}>
            <Crown size={20} style={{ color: GOLD }} className="mb-2" />
            <p className="text-white text-sm font-bold mb-1">Recovery+</p>
            <p className="text-xs mb-3" style={{ color: "rgba(255,255,255,0.6)" }}>
              Unlimited messages & see who likes you
            </p>
            <button
              onClick={() => navigate("/subscription")}
              className="w-full rounded-full py-2 text-xs font-extrabold transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: GOLD, color: NAVY }}
              data-testid="sidebar-upgrade-btn"
            >
              Upgrade
            </button>
          </div>
        )}

        <div className="px-5 py-5 flex items-center gap-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <button onClick={() => navigate("/profile")} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid="sidebar-user-chip">
            {photo ? (
              <img src={photo} alt="" className="w-9 h-9 rounded-full object-cover border-2 flex-shrink-0" style={{ borderColor: GOLD }} />
            ) : (
              <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0" style={{ backgroundColor: GOLD, color: NAVY }}>
                {(user?.name || "?").charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-white text-sm font-bold leading-none truncate">
                {user?.name}{user?.age ? `, ${user.age}` : ""}
              </p>
              {cleanLabel && (
                <p className="text-[10px] mt-1 font-bold uppercase tracking-wider" style={{ ...mono, color: GOLD }}>
                  {cleanLabel}
                </p>
              )}
            </div>
          </button>
          <button
            onClick={logout}
            className="p-2 rounded-full transition-colors hover:bg-white/10 flex-shrink-0"
            style={{ color: "rgba(255,255,255,0.5)" }}
            title="Log out"
            data-testid="sidebar-logout-btn"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className={`lg:pl-64 ${isChat ? "" : "min-h-screen"}`}>
        <MerchLaunchBanner />
        {children}
      </div>
    </div>
  );
};

export default AppShell;
